import { PlusIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { DragDropList } from '@/components/ui/custom/dataInput/DragDrop';
import { Button } from '@/components/ui/daisy';
import useAssessments from '@/data/hook/evaluation/useAssessments';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import useOptimisticReorder from '@/data/hook/useOptimisticReorder';
import { Assessment } from '@/models/Assessment';
import Evaluation from '@/models/Evaluation';
import AssessmentCard from './AssessmentCard';

type AssessmentsListProps = {
  evaluation: Evaluation;
  cacheManager: ListCacheManager<Evaluation>;
  onAddAssessment: () => void;
  onEditAssessment: (assessment: Assessment) => void;
};

export default function AssessmentsList({
  evaluation,
  cacheManager,
  onAddAssessment,
  onEditAssessment,
}: AssessmentsListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });

  const {
    assessments,
    reorderAssessments,
    isReordering,
    deleteAssessment,
    isDeleting,
  } = useAssessments({ evaluation, cacheManager });

  const { optimistic: optimisticAssessments, handleDragEnd } =
    useOptimisticReorder({
      items: assessments,
      isReordering,
      onReorder: reorderAssessments,
    });

  return (
    <div className="flex flex-col">
      <ConditionalRenderer condition={optimisticAssessments.length > 0}>
        <DragDropList
          groupId={`assessments-list-${evaluation.id}`}
          onDragEnd={handleDragEnd}
          disabled={isReordering}
          className="gap-0"
        >
          {optimisticAssessments.map((assessment, index) => (
            <AssessmentCard
              key={assessment.id}
              assessment={assessment}
              index={index}
              isDraggable
              id={String(assessment.id)}
              className="mb-6"
              isDeleting={isDeleting}
              onEdit={() => onEditAssessment(assessment)}
              onDelete={deleteAssessment}
            />
          ))}
        </DragDropList>
      </ConditionalRenderer>
      <FieldsetContainer>
        <Button color="primary" className="w-full" onClick={onAddAssessment}>
          <PlusIcon className="w-4 h-4" />
          {t('addAssessment')}
        </Button>
      </FieldsetContainer>
    </div>
  );
}
